import React, { FC } from 'react';
import { BlobProvider } from '@react-pdf/renderer';
import {
  ArrowsExpandIcon,
  CheckCircleIcon,
  ClipboardListIcon,
  DocumentTextIcon,
  DownloadIcon,
  XCircleIcon,
} from '@heroicons/react/outline';

import {
  ActivityType,
  ProgramActivityDataFragment,
  UserType,
  useCreateProgramNoteMutation,
  useProgramAssessmentLazyQuery,
} from '../../../generated/graphql';

import { programActivityOpen } from '../../lib/programActivity';
import { getProgramActivityStatus } from '../ProgramActivityStatus';
import { handlePdfDownload } from '../../lib/pdf';
import ProgramAssessmentPDF from '../ProgramActivitySlideover/ProgramAssessmentPDF';
import { createNoteFromForm } from '../../lib/createNoteFromForm';
import { customToast } from '../ToastAlert/customToast';
import ActionButton from '../ActionButton';
import ActionButtonGroup from '../ActionButtonGroup';

type ActionButtonsProps = {
  programActivity: ProgramActivityDataFragment;
  userType?: UserType;
  authedProviderUserId?: string;
  patientName?: string;
  programId?: string;
  onAssessmentOpen?: (programAssessmentId: string) => void;
  onTextOpen?: (
    activityText: ProgramActivityDataFragment['activityText'],
  ) => void;
  onMarkComplete?: () => void;
  onFillOut?: () => void;
  fillOutTitle?: string;
  isCompletingProgramActivity?: boolean;
  onDismiss?: () => void;
  onVoided?: () => Promise<unknown>;
  onExpandResponses?: () => void;
};

const ActionButtons: FC<ActionButtonsProps> = ({
  programActivity,
  userType = UserType.ProviderUser,
  authedProviderUserId,
  patientName,
  programId,
  onAssessmentOpen,
  onTextOpen,
  onMarkComplete,
  onFillOut,
  fillOutTitle = 'Fill out',
  isCompletingProgramActivity = false,
  onDismiss,
  onVoided,
  onExpandResponses,
}) => {
  const isProviderUser = userType === UserType.ProviderUser;
  const programActivityStatus = getProgramActivityStatus(
    programActivity,
    userType,
  );

  // Create note mutation
  const [createProgramNote] = useCreateProgramNoteMutation();

  // Lazy query to fetch full program assessment data (including answers)
  const [getProgramAssessment] = useProgramAssessmentLazyQuery();

  // Handle creating a note from form
  const handleCreateNote = async () => {
    if (!programActivity.programAssessment || !programId) {
      customToast.error('Unable to create note: missing data');
      return;
    }

    try {
      // Fetch the full program assessment data including answers
      const { data } = await getProgramAssessment({
        variables: {
          programAssessmentId: programActivity.programAssessment.id,
        },
      });

      if (!data?.programAssessment) {
        customToast.error('Unable to fetch form data for note creation');
        return;
      }

      // Use the full program assessment data from the query
      await createNoteFromForm({
        programId,
        programAssessment: data.programAssessment as any,
        patientFirstName: patientName?.split(' ')[0],
        createProgramNote,
        onSuccess: (noteTitle: string) => {
          customToast.success(`Note "${noteTitle}" created successfully`);
        },
      });
    } catch (error) {
      console.error('Error fetching program assessment:', error);
      customToast.error('Unable to fetch form data for note creation');
    }
  };

  // Get actions from programActivityOpen
  const { onOpen, openIcon, secondaryOnOpen, secondaryOpenIcon } =
    programActivityOpen(programActivity, onAssessmentOpen, onTextOpen);

  // Determine which buttons to show
  const showExpandResponses =
    isProviderUser &&
    programActivity?.activityType === ActivityType.Assessment &&
    Boolean(programActivity?.programAssessment) &&
    programActivityStatus !== 'Delivered' &&
    programActivityStatus !== 'Seen';

  const showDownloadPdf =
    Boolean(programActivity?.programAssessment?.completedAt) &&
    programActivityStatus === 'Completed' &&
    Boolean(programActivity?.programAssessment);

  const showCreateNote =
    isProviderUser &&
    programActivity?.activityType === ActivityType.Assessment &&
    Boolean(programActivity?.programAssessment) &&
    Boolean(programActivity?.programAssessment?.completedAt);

  const allowCompletion =
    onMarkComplete &&
    programActivity?.activityType !== ActivityType.Assessment &&
    programActivity?.activityType !== ActivityType.PdfForm &&
    programActivity?.activityType !== ActivityType.Esign &&
    programActivity?.isCompletable &&
    !programActivity?.completedAt;

  const allowDismissal =
    onDismiss &&
    programActivity?.isCompletable &&
    !programActivity?.completedAt &&
    !programActivity?.dismissedAt &&
    !programActivity?.voidedAt;

  // Check if any actions are available
  const hasAnyActions =
    showExpandResponses ||
    showDownloadPdf ||
    showCreateNote ||
    (onOpen && openIcon) ||
    (secondaryOnOpen && secondaryOpenIcon) ||
    Boolean(onFillOut) ||
    allowCompletion ||
    allowDismissal;

  return (
    <ActionButtonGroup>
      {/* Fill out button */}
      {onFillOut && (
        <ActionButton
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onFillOut();
          }}
          title={fillOutTitle}
        >
          <ClipboardListIcon className="h-5 w-5 text-neutral-125" />
        </ActionButton>
      )}

      {/* Complete button */}
      {allowCompletion && (
        <ActionButton
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onMarkComplete?.();
          }}
          title="Complete"
          disabled={isCompletingProgramActivity}
        >
          <CheckCircleIcon className="h-5 w-5 text-neutral-125" />
        </ActionButton>
      )}

      {/* Expand Responses button */}
      {showExpandResponses && onExpandResponses && (
        <ActionButton
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onExpandResponses();
          }}
          title="Expand Responses"
        >
          <ArrowsExpandIcon className="h-5 w-5 text-neutral-125" />
        </ActionButton>
      )}

      {/* Download PDF button */}
      {showDownloadPdf && programActivity?.programAssessment && (
        <BlobProvider
          document={
            <ProgramAssessmentPDF
              programAssessment={programActivity.programAssessment as any}
              isProviderUser={isProviderUser}
            />
          }
        >
          {({ blob, loading, error }) => (
            <ActionButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (blob && programActivity?.programAssessment) {
                  handlePdfDownload(
                    blob,
                    programActivity.programAssessment as any,
                    patientName || 'Client',
                    () => console.log('PDF downloaded successfully'),
                  );
                }
              }}
              title={loading ? 'Generating PDF...' : 'Download PDF'}
              disabled={loading || Boolean(error)}
            >
              <DownloadIcon className="h-5 w-5 text-neutral-125" />
            </ActionButton>
          )}
        </BlobProvider>
      )}

      {/* Create Private Note button */}
      {showCreateNote && (
        <ActionButton
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleCreateNote();
          }}
          title="Create Private Note"
        >
          <DocumentTextIcon className="h-5 w-5 text-neutral-125" />
        </ActionButton>
      )}

      {/* Primary action button (Open/View) */}
      {onOpen && openIcon && (
        <ActionButton
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpen();
          }}
          title="Open"
        >
          {React.createElement(openIcon, {
            className: 'h-5 w-5 text-neutral-125',
          })}
        </ActionButton>
      )}

      {/* Secondary action button (Assessment details) */}
      {secondaryOnOpen && secondaryOpenIcon && (
        <ActionButton
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            secondaryOnOpen();
          }}
          title="Assessment Details"
        >
          {React.createElement(secondaryOpenIcon, {
            className: 'h-5 w-5 text-neutral-125',
          })}
        </ActionButton>
      )}

      {/* Dismiss button */}
      {allowDismissal && (
        <ActionButton
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onDismiss?.();
          }}
          title="Dismiss"
        >
          <XCircleIcon className="h-5 w-5 text-neutral-125" />
        </ActionButton>
      )}

      {/* Empty state when no actions are available */}
      {!hasAnyActions && (
        <span className="text-caption text-neutral-110">-</span>
      )}
    </ActionButtonGroup>
  );
};

export default ActionButtons;
